import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';
import { useUnreadCount, useNotificationsUnreadCount } from '../../lib/hooks';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Bell, User, LogOut, Settings, ChevronDown, MessageSquare, Languages } from 'lucide-react';
import NotificationCenter from './NotificationCenter';

export const Topbar = ({ pageTitle }) => {
  const { user, logout } = useAuth();
  const { lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  
  const { data: unreadData } = useUnreadCount();
  const { data: notificationsData } = useNotificationsUnreadCount();
  
  const unreadMessageCount = unreadData?.count || 0;
  const notificationCount = notificationsData?.count || 0;
  
  const avatarUrl = user?.avatar_url 
    ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}?t=${Date.now()}`
    : null;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <header className="sticky top-0 z-30 bg-white border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left side - App name and page title */}
          <div className="flex items-center gap-4 pl-12 md:pl-0">
            <div>
              <h1 className="font-heading text-xl font-bold text-foreground">{pageTitle}</h1>
            </div>
          </div>

          {/* Right side - Language, Messages, Notifications and User */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 h-9 font-medium"
              data-testid="lang-toggle-btn"
            >
              <Languages className="w-4 h-4" />
              <span className="text-xs font-bold">{lang === 'fr' ? 'DE' : 'FR'}</span>
            </Button>

            {/* Messages Icon */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => navigate('/messages')}
              data-testid="messages-icon"
            >
              <MessageSquare className="w-5 h-5 text-muted-foreground" />
              {unreadMessageCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-secondary text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                </span>
              )}
            </Button>
            
            {/* Notification Bell */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setNotificationCenterOpen(true)}
              data-testid="notification-bell"
            >
              <Bell className="w-5 h-5 text-muted-foreground" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </Button>

            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-3 px-3 py-2 h-auto"
                  data-testid="user-dropdown-trigger"
                >
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={avatarUrl} />
                    <AvatarFallback className="bg-primary text-white font-semibold text-sm">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {t(`role_${user?.role}`, lang)}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2 sm:hidden">
                  <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="sm:hidden" />
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="profile-menu-item">
                  <User className="w-4 h-4 mr-2" />
                  {t('settings_profile', lang)}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                  <Settings className="w-4 h-4 mr-2" />
                  {t('settings_title', lang)}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('sidebar_logout', lang)}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      
      {/* Notification Center */}
      <NotificationCenter 
        open={notificationCenterOpen} 
        onOpenChange={setNotificationCenterOpen} 
      />
    </>
  );
};

export default Topbar;
